<template>
  <b-col class="px-0">
    <confirm-modal
      v-if="showExcelDownloadModal"
      id="excel-download-modal"
      title="Information"
      :confirmation="confirmModalMessage"
      ok-button="Close"
      close-button=""
      @submit="showExcelDownloadModal = false"
    />
    <parcel-event-history-modal
      v-if="selectedParcel"
      :parcel="selectedParcel"
    />
    <edit-parcel-items
      v-if="selectedParcel"
      id="edit-parcel-items"
      :parcel="selectedParcel"
      :declaration="selectedDeclaration"
      :shipment="selectedParcel ? selectedParcel.shipment : null"
      readonly
    />
    <b-col class="text-right px-0 pb-2">
      <b-button
        :disabled="warehouseExcelLoading"
        variant="outline-primary"
        @click="handleWarehouseExcelDownload"
      >
        Download Excel for warehouse
        <b-spinner
          v-if="warehouseExcelLoading"
          small
          class="ml-1"
        />
      </b-button>
      <b-button
        :disabled="excelLoading"
        variant="outline-primary"
        class="m-1"
        @click="handleExcelDownload"
      >
        Download Excel
        <b-spinner
          v-if="excelLoading"
          small
          class="ml-1"
        />
      </b-button>
    </b-col>
    <b-card
      no-body
      class="parcels-table"
    >
      <b-card-actions
        action-refresh
        @refresh="fetchData()"
      >
        <div class="overflow-x-scroll">
          <b-table
            :items="computedTableData"
            responsive
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="isSortDirDesc"
            :busy="loading"
            stacked="sm"
            class="mb-0"
            show-empty
            :tbody-tr-class="rowClass"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-1" />
                <strong>Loading...</strong>
              </div>
            </template>

            <template
              v-if="showLogoColumn"
              #cell(logo)="data"
            >
              <CompanyThumb
                v-if="data.item.company"
                :logo="data.item.company.logo"
                :name="data.item.company.name"
              />
            </template>

            <template #cell(low_value_declaration.status)="data">
              <div
                v-if="data.item.low_value_declaration"
                class="d-flex align-items-center"
              >
                <b-badge
                  v-if="getStatusInfo(data.item.low_value_declaration.status, 'variant')"
                  pill
                  :variant="getStatusInfo(data.item.low_value_declaration.status, 'variant')"
                >
                  {{ getStatusInfo(data.item.low_value_declaration.status, 'label') }}
                </b-badge>
              </div>
            </template>

            <template #cell(shipment.document_number)="data">
              <router-link :to="{ name: 'shipment', params: { shipmentId: data.item.shipment.id } }">
                {{ data.item.shipment.document_number }}
              </router-link>
            </template>

            <template #cell(low_value_declaration.created_at)="data">
              <div
                v-if="data.item.low_value_declaration"
                class="d-flex align-items-center"
              >
                {{ data.item.low_value_declaration.created_at | formatDate }}
              </div>
            </template>

            <template #cell(low_value_declaration.updated_at)="data">
              <div
                v-if="data.item.low_value_declaration"
                class="d-flex align-items-center"
              >
                {{ data.item.low_value_declaration.updated_at | formatDate }}
              </div>
            </template>

            <!-- Column: Actions -->
            <template
              #cell(actions)="data"
              class="px-1"
            >
              <div class="d-flex align-items-center">
                <b-button-group>
                  <view-parcel-button
                    :declaration="data.item.low_value_declaration"
                    :parcel="data.item"
                  />
                  <download-pdf-button
                    :declaration="data.item.low_value_declaration"
                    :parcel="data.item"
                    :shipment="data.item.shipment"
                  />
                  <b-button
                    v-b-modal.parcel-event-history
                    type="button"
                    class="showEventsHistoryModal"
                    variant="primary"
                    size="sm"
                    @click="selectedParcel = data.item"
                  >
                    <b-icon-clock-history />
                  </b-button>
                </b-button-group>
                <b-icon-archive
                  v-if="data.item.is_archived"
                  v-b-tooltip.hover.top="'Buyer personal data is removed'"
                  class="ml-1 cursor-pointer text-secondary"
                />
              </div>
            </template>
          </b-table>
        </div>
        <pagination />
      </b-card-actions>
    </b-card>
  </b-col>
</template>

<script>
import {
  BCard, BTable, BBadge, BCol, VBTooltip, BButtonGroup, BSpinner, BIconArchive, BIconClockHistory, BButton,
} from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import handleError from '@/views/components/errorHandler'
import Pagination from '../../layouts/components/Pagination/Pagination.vue'
import ViewParcelButton from '@/views/shipments/declarations/components/ViewParcelButton.vue'
import DownloadPdfButton from '@/views/shipments/declarations/components/DownloadPdfButton.vue'
import CompanyThumb from '@/views/components/CompanyThumb.vue'
import EditParcelItems from '@/views/shipments/declarations/modals/EditParcelItems.vue'
import { searchQuery } from '@core/utils/filter'
import { EventBus } from '@core/utils/utils'
import ParcelEventHistoryModal from '@/views/parcels/ParcelEventHistoryModal.vue'
import ConfirmModal from '@core/components/confirmation/confirmModal.vue'
import excelDownloadMixin from '@/mixins/excelDownloadMixin'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    ConfirmModal,
    ParcelEventHistoryModal,
    BIconArchive,
    EditParcelItems,
    BSpinner,
    CompanyThumb,
    BCard,
    BCardActions,
    BTable,
    BBadge,
    BCol,
    BButton,
    BButtonGroup,
    Pagination,
    ViewParcelButton,
    DownloadPdfButton,
    BIconClockHistory,
  },
  mixins: [excelDownloadMixin],
  props: ['tableData', 'statuses', 'showLogoColumn'],
  data() {
    return {
      page: 1,
      perPage: 30,
      tableDataStore: [],
      loading: false,
      parcels: [],
      sortBy: null,
      isSortDirDesc: null,
      filters: {},
      fields: [
        { key: 'actions', label: 'Actions' },
        ...(this.showLogoColumn ? [{ key: 'logo', label: 'Company', sortable: false, class: 'image-column' }] : []),
        { key: 'tracking_number', label: 'Tracking number', sortable: false },
        { key: 'low_value_declaration.status', label: 'Status', sortable: false },
        { key: 'container_code', label: 'Container', sortable: false },
        { key: 'shipment.document_number', label: 'Document number', sortable: false },
        { key: 'low_value_declaration.lrn', label: 'LRN', sortable: false },
        { key: 'low_value_declaration.mrn', label: 'MRN', sortable: false },
        {
          key: 'low_value_declaration.created_at',
          label: 'Declaration time',
          sortable: true,
          thClass: 'd-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
        },
        {
          key: 'low_value_declaration.updated_at',
          label: 'Last event',
          sortable: true,
          thClass: 'd-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
        },
      ],
      selectedDeclaration: null,
      selectedParcel: null,
      indexUrl: `/v1/companies/${this.$activeCompany().data.company.id}/parcels`,
      downloadWarehouseExcelUrl: `${process.env.VUE_APP_API_URL}/v1/companies/${this.$activeCompany().data.company.id}/parcels/warehouse-excel`,
      downloadExcelUrl: `${process.env.VUE_APP_API_URL}/v1/companies/${this.$activeCompany().data.company.id}/parcels/excel`,
      additionalStatuses: [
        { label: 'Needs attention', value: 'unpaid', variant: 'warning' },
        { label: 'Needs attention', value: 'errored', variant: 'warning' },
      ],
    }
  },
  computed: {
    computedTableData() {
      return this.tableDataStore
    },
    warehouseExcelUrl() {
      return searchQuery(this.downloadWarehouseExcelUrl, this.filters)
    },
    excelDownloadUrl() {
      return searchQuery(this.downloadExcelUrl, this.filters)
    },
  },
  watch: {
    sortBy() {
      this.fetchData()
    },
    isSortDirDesc() {
      this.fetchData()
    },
  },
  mounted() {
    EventBus.$on('updateData', this.handleUpdateData)
    EventBus.$on('updateFilters', this.handleUpdateFilters)
    this.tableDataStore = this.$props.tableData
    EventBus.$on('pageChange', this.handlePageChange)
    EventBus.$on('set-view-parcel', this.viewParcel)
    EventBus.$on('update-parcel-items', this.handleUpdateParcelItems)
  },
  beforeDestroy() {
    EventBus.$off('updateData', this.handleUpdateData)
    EventBus.$off('updateFilters', this.handleUpdateFilters)
    EventBus.$off('pageChange', this.handlePageChange)
    EventBus.$off('set-view-parcel', this.viewParcel)
    EventBus.$off('update-parcel-items', this.handleUpdateParcelItems)
  },
  methods: {
    handleUpdateData(data) {
      this.tableDataStore = Object.values(data)
    },
    handleUpdateFilters(data) {
      this.page = 1
      this.filters = data
      this.fetchData()
    },
    handlePageChange(newPage) {
      this.page = newPage
      this.fetchData()
    },
    handleUpdateParcelItems() {
      this.selectedDeclaration = null
      this.selectedParcel = null
      this.fetchData()
    },
    rowClass(item) {
      return item && item.is_archived ? 'archived-row muted' : ''
    },
    viewParcel(declaration, parcel) {
      this.selectedDeclaration = declaration
      this.selectedParcel = parcel
      this.$nextTick(() => {
        this.$bvModal.show('edit-parcel-items')
      })
    },
    fetchData() {
      this.loading = true

      this.filters = { ...this.filters, sortBy: this.sortBy, sortDesc: this.isSortDirDesc, page: this.page, perPage: this.perPage }

      // Request
      this.$http.get(searchQuery(this.indexUrl, this.filters))
        .then(response => {
          this.$data.parcels = response.data.data
          EventBus.$emit('updateData', response.data.data)
          EventBus.$emit('updateMeta', response.data.meta)
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
        .finally(() => {
          this.loading = false
        })
    },
    getStatusInfo(statusCode, field) {
      let filteredStatus = this.statuses.find(status => status.value === statusCode)

      if (!filteredStatus) {
        filteredStatus = this.additionalStatuses.find(status => status.value === statusCode)
      }

      return filteredStatus ? filteredStatus[field] : null
    },
  },
}
</script>

<style>
  .parcels-table .table td[data-label="Actions"] {
    padding: .72rem !important;
  }
</style>
